import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import { useAdminLocationGroups, useAdminLocations, useCreateAdminLocation, } from '@/api/opengater/useLocations';
import { Input } from '@/components/primitives/Input';
import { Select } from '@/components/primitives/Select';
import { Toggle } from '@/components/primitives/Toggle';
import { SidePanel } from '@/components/SidePanel';
import { PanelForm, SaveBar, SettingsGroup, SettingsNote, SettingsRow, } from '@/components/settings/Settings';
import { createdEntityId } from './creationResult';
import { DirtyNavigationGuard } from './DirtyNavigationGuard';
import { locationAdminError, locationFieldErrors, requiredTextError } from './locationRules';
import s from '../OpengaterLocations.module.css';
const initialDraft = {
    flag: '',
    name: 'Новая локация',
    description: '',
    speed: '',
    is_premium: false,
    is_active: true,
    group_id: '',
    group_order: '',
    is_geo_routed: false,
};
function normalized(draft) {
    return {
        flag: draft.flag.trim() || null,
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        speed: draft.speed.trim() === '' ? null : Number(draft.speed),
        is_premium: draft.is_premium,
        is_active: draft.is_active,
        group_id: draft.group_id === '' ? null : Number(draft.group_id),
        group_order: draft.group_order.trim() === '' ? null : Number(draft.group_order),
        is_geo_routed: draft.is_geo_routed,
    };
}
export function LocationCreateForm({ onCreated, onClose, }) {
    const navigate = useNavigate();
    const catalog = useAdminLocations('all');
    const groups = useAdminLocationGroups();
    const create = useCreateAdminLocation();
    const [draft, setDraft] = useState(initialDraft);
    const [fieldErrors, setFieldErrors] = useState({});
    const [saveError, setSaveError] = useState(null);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const dirty = useMemo(() => Object.keys(initialDraft).some((key) => draft[key] !== initialDraft[key]), [draft]);
    const requestNavigation = useCallback((action) => {
        if (dirty)
            setPendingNavigation(() => action);
        else
            action();
    }, [dirty]);
    useBeforeUnload(useCallback((event) => {
        if (!dirty)
            return;
        event.preventDefault();
        event.returnValue = '';
    }, [dirty]));
    useEffect(() => {
        if (!dirty)
            return undefined;
        const guardLinkNavigation = (event) => {
            if (event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey) {
                return;
            }
            const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
            if (!(anchor instanceof HTMLAnchorElement) || anchor.download || anchor.target)
                return;
            const target = new URL(anchor.href, window.location.href);
            if (target.origin !== window.location.origin)
                return;
            const href = `${target.pathname}${target.search}${target.hash}`;
            const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            if (href === current)
                return;
            event.preventDefault();
            event.stopPropagation();
            requestNavigation(() => navigate(href));
        };
        document.addEventListener('click', guardLinkNavigation, true);
        return () => document.removeEventListener('click', guardLinkNavigation, true);
    }, [dirty, navigate, requestNavigation]);
    const change = (key, value) => {
        setDraft((current) => ({ ...current, [key]: value }));
        setFieldErrors((current) => ({ ...current, [key]: undefined }));
        setSaveError(null);
        create.reset();
    };
    const nameError = requiredTextError(draft.name) ?? fieldErrors.name;
    const speedError = draft.speed.trim() !== '' && !Number.isInteger(Number(draft.speed))
        ? 'Укажите целое число'
        : fieldErrors.speed;
    const orderError = draft.group_order.trim() !== '' && !Number.isInteger(Number(draft.group_order))
        ? 'Укажите целый порядок'
        : fieldErrors.group_order;
    const submit = async (event) => {
        event.preventDefault();
        if (nameError || speedError || orderError || create.isPending)
            return;
        try {
            const response = await create.mutateAsync(normalized(draft));
            try {
                await catalog.refetch({ throwOnError: true });
            }
            catch {
                // A returned id is enough to open the card; otherwise the invalidated list retries normally.
            }
            onCreated(createdEntityId(response, [], []), draft.is_active);
        }
        catch (error) {
            const fields = locationFieldErrors(error, Object.keys(initialDraft));
            setFieldErrors(fields);
            setSaveError(Object.keys(fields).length ? null : locationAdminError(error));
        }
    };
    return (_jsx(SidePanel, { title: "\u041D\u043E\u0432\u0430\u044F \u043B\u043E\u043A\u0430\u0446\u0438\u044F", onClose: () => requestNavigation(onClose), children: _jsxs("div", { className: s.panelContent, children: [_jsx(DirtyNavigationGuard, { open: pendingNavigation != null, onCancel: () => setPendingNavigation(null), onConfirm: () => {
                        const action = pendingNavigation;
                        setPendingNavigation(null);
                        action?.();
                    } }), _jsxs(PanelForm, { meta: "\u041D\u043E\u0432\u0430\u044F \u0437\u0430\u043F\u0438\u0441\u044C \u043A\u0430\u0442\u0430\u043B\u043E\u0433\u0430", onSubmit: submit, children: [_jsxs(SettingsGroup, { title: "\u041E\u0441\u043D\u043E\u0432\u043D\u043E\u0435", children: [_jsx(SettingsRow, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", wide: true, error: nameError, children: _jsx(Input, { "aria-label": "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u043B\u043E\u043A\u0430\u0446\u0438\u0438", "aria-invalid": Boolean(nameError), value: draft.name, disabled: create.isPending, onChange: (event) => change('name', event.target.value) }) }), _jsx(SettingsRow, { label: "\u0424\u043B\u0430\u0433", hint: "\u041A\u043E\u0434 \u0438\u043B\u0438 \u044D\u043C\u043E\u0434\u0437\u0438", error: fieldErrors.flag, children: _jsx(Input, { "aria-label": "\u0424\u043B\u0430\u0433", value: draft.flag, disabled: create.isPending, onChange: (event) => change('flag', event.target.value) }) }), _jsx(SettingsRow, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", wide: true, error: fieldErrors.description, children: _jsx(Input, { "aria-label": "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u043B\u043E\u043A\u0430\u0446\u0438\u0438", value: draft.description, disabled: create.isPending, onChange: (event) => change('description', event.target.value) }) }), _jsx(SettingsRow, { label: "\u0421\u043A\u043E\u0440\u043E\u0441\u0442\u044C", suffix: "\u041C\u0431\u0438\u0442/\u0441", error: speedError, children: _jsx(Input, { "aria-label": "\u0421\u043A\u043E\u0440\u043E\u0441\u0442\u044C", type: "number", value: draft.speed, disabled: create.isPending, onChange: (event) => change('speed', event.target.value) }) })] }), _jsxs(SettingsGroup, { title: "\u0420\u0430\u0437\u043C\u0435\u0449\u0435\u043D\u0438\u0435", children: [_jsx(SettingsRow, { label: "\u0413\u0440\u0443\u043F\u043F\u0430", wide: true, error: fieldErrors.group_id, children: _jsxs(Select, { "aria-label": "\u0413\u0440\u0443\u043F\u043F\u0430", value: draft.group_id, disabled: create.isPending, onChange: (event) => change('group_id', event.target.value), children: [_jsx("option", { value: "", children: "\u0411\u0435\u0437 \u0433\u0440\u0443\u043F\u043F\u044B" }), (groups.data ?? []).map((group) => (_jsx("option", { value: group.id, children: group.name }, group.id)))] }) }), _jsx(SettingsRow, { label: "\u041F\u043E\u0440\u044F\u0434\u043E\u043A \u0432 \u0433\u0440\u0443\u043F\u043F\u0435", error: orderError, children: _jsx(Input, { "aria-label": "\u041F\u043E\u0440\u044F\u0434\u043E\u043A \u0432 \u0433\u0440\u0443\u043F\u043F\u0435", type: "number", value: draft.group_order, disabled: create.isPending, onChange: (event) => change('group_order', event.target.value) }) })] }), _jsxs(SettingsGroup, { title: "\u0414\u043E\u0441\u0442\u0443\u043F\u043D\u043E\u0441\u0442\u044C", children: [_jsx(SettingsRow, { label: "\u041F\u0440\u0435\u043C\u0438\u0430\u043B\u044C\u043D\u0430\u044F", error: fieldErrors.is_premium, children: _jsx(Toggle, { "aria-label": "\u041F\u0440\u0435\u043C\u0438\u0430\u043B\u044C\u043D\u0430\u044F", on: draft.is_premium, disabled: create.isPending, onChange: (value) => change('is_premium', value) }) }), _jsx(SettingsRow, { label: "\u0410\u043A\u0442\u0438\u0432\u043D\u0430", error: fieldErrors.is_active, children: _jsx(Toggle, { "aria-label": "\u0410\u043A\u0442\u0438\u0432\u043D\u0430", on: draft.is_active, disabled: create.isPending, onChange: (value) => change('is_active', value) }) }), _jsx(SettingsRow, { label: "\u0413\u0435\u043E\u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0438\u0437\u0430\u0446\u0438\u044F", error: fieldErrors.is_geo_routed, children: _jsx(Toggle, { "aria-label": "\u0413\u0435\u043E\u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0438\u0437\u0430\u0446\u0438\u044F", on: draft.is_geo_routed, disabled: create.isPending, onChange: (value) => change('is_geo_routed', value) }) })] }), (saveError || create.error) && (_jsx(SettingsNote, { tone: "error", children: saveError ?? locationAdminError(create.error) })), _jsx(SaveBar, { dirty: true, saving: create.isPending, saved: false, canSave: !nameError && !speedError && !orderError && !create.isPending, error: saveError ?? undefined, text: "\u041D\u043E\u0432\u0430\u044F \u043B\u043E\u043A\u0430\u0446\u0438\u044F", submitLabel: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C", onReset: onClose })] })] }) }));
}
